import React, { useState, useMemo, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Select,
  Input,
  DatePicker,
  message,
  InputNumber,
  Modal,
  Form,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { Text, View } from 'styles/reactnative';
import {
  CloseOutlined,
  SearchOutlined,
  EditOutlined,
  CheckOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Layout } from 'antd';
import { HeaderSection } from 'app/components/Layouts';
import ModernTabNavigation from 'app/components/ModernTabNavigation';
import API from 'utils/api';
import { useToken } from 'app/hooks/auth/token';
import { useWindowDimensions } from 'app/hooks';
import { API_URL } from 'app/constants/APIKEYS';
import { t } from 'i18next';
import Search from 'antd/es/input/Search';
import usePagination from 'app/hooks/usePagination';
import ManualWithdrawalModal from './ManualWithdrawalModal';
import UserTag from 'app/components/UserTag';
import KrwPrice from 'app/components/PositionColumns/KrwPrice';
import AdminUserModal from 'app/components/AdminUserModal';

const { Content } = Layout;

// Payment API 응답 타입
interface WithdrawData {
  id: number;
  uid?: string;
  user_id: number;
  request_type: string;
  amount: string;
  order_id: string;
  status: string;
  pg_provider?: string;
  pg_tid?: string;
  data?: any;
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
  level?: number;
  group_name?: string;
  nickname?: any;
  fullname?: any;
  email?: any;
  phone?: any;
  loginid?: string;
  // 추가된 사용자 정보
  userInfo?: {
    nickname?: string;
    fullname?: string;
    email?: string;
    phone?: string;
    level?: number;
    group_name?: string;
    uid?: string;
    loginid?: string;
    bank_name?: string;
    account_number?: string;
    account_holder?: string;
  };
}

// Supra API 응답 타입
interface SupraWithdrawData {
  id: number;
  request_id: string;
  request_type: string;
  shopid: string;
  userid: string;
  qty: string;
  in_fee: string;
  out_fee: string;
  send_qty: string;
  wallet_address: string;
  transfer_type: string;
  distributor_id: string;
  status: string;
  tx_hash: string | null;
  otc_link: string | null;
  reason: string | null;
  created_at: string;
  updated_at: string;
  level?: number;
}

// 통합된 테이블 데이터 타입
interface CombinedWithdrawData {
  id: number | string;
  uid?: string | number;
  user_id: string | number;
  amount: string;
  order_id?: string;
  status: string;
  pg_provider?: string;
  pg_tid?: string;
  data?: any;
  created_at: string;
  updated_at?: string;
  completed_at?: string | null;
  level?: number;
  group_name?: string;
  source: 'payment' | 'supra' | 'matched'; // 데이터 출처
  request_id?: string;
  send_qty?: string;
  out_fee?: string;
  reason?: string | null;
  // 매칭된 데이터의 경우 추가 정보
  payment_id?: number;
  supra_id?: number;
  supra_status?: string;
  supra_updated_at?: string;
  otc_link?: string | null;
  nickname?: any;
  fullname?: any;
  email?: any;
  phone?: any;
  loginid?: string;
  // 추가된 사용자 정보
  userInfo?: {
    nickname?: string;
    fullname?: string;
    email?: string;
    phone?: string;
    level?: number;
    group_name?: string;
    uid?: string;
    loginid?: string;
    bank_name?: string;
    account_number?: string;
    account_holder?: string;
  };
}

interface ApiResponse {
  data: WithdrawData[];
  total: number;
  page: number;
  pageSize: number;
}

const PGWithdrawPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState('');
  const [status, setStatus] = useState('');
  const [startDate, setStartDate] = useState<string | null>(
    dayjs().subtract(30, 'day').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState<string | null>(
    dayjs().format('YYYY-MM-DD')
  );
  const [data, setData] = useState<CombinedWithdrawData[]>([]);
  const { data: token } = useToken();
  const { width } = useWindowDimensions();

  const [showModal, setShowModal] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [showManualWithdrawalModal, setShowManualWithdrawalModal] = useState(false);
  const [showOnlyMatched, setShowOnlyMatched] = useState(true); // 매칭된 데이터만 표시할지 여부
  const [showPriceEditModal, setShowPriceEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<CombinedWithdrawData | null>(null);
  const [searchType, setSearchType] = useState('id');
  const [searchWord, setSearchWord] = useState('');

  const { page, pageSize, totalCount, setPage, setPageSize, setTotalCount } =
    usePagination();

  // Supra API 하드코딩된 인증키
  const SUPRA_AUTH_KEY =
    'a7dd5ad1e5319bc828c1ca1d670aa85100244ba001d81224c599c9e33ed0c5d1';

  // 모든 데이터를 가져와서 클라이언트에서 페이지네이션 처리
  const [allCombinedData, setAllCombinedData] = useState<CombinedWithdrawData[]>([]);

  // 사용자 정보를 가져오는 함수 (개별 API 호출)
  const fetchUserInfo = async (userIds: number[]) => {
    if (!token || userIds.length === 0) return {};

    try {
      const userInfoMap: { [key: number]: any } = {};
      
      // 각 사용자 ID에 대해 개별적으로 API 호출
      await Promise.all(
        userIds.map(async (userId) => {
          try {
            const userResponse = await API.get(
              `/admin/user/${userId}`,
              token + '',
              {}
            );

             if (userResponse.result?.success) {
               const userData = userResponse.result.result;
               userInfoMap[userId] = {
                 uid: userData.uid || String(userData.id),
                 loginid: userData.loginid,
                 nickname: userData.nickname,
                 fullname: userData.fullname,
                 email: userData.email,
                 phone: userData.phone,
                 level: userData.level,
                 group_name: userData.group?.split('|')[0] || null,
                 bank_name: userData.bank_name,
                 account_number: userData.account_number,
                 account_holder: userData.account_holder,
               };
             }
          } catch (error) {
            console.error(`사용자 ${userId} 정보 조회 오류:`, error);
          }
        })
      );

      return userInfoMap;
    } catch (error) {
      console.error('사용자 정보 조회 오류:', error);
    }
    return {};
  };

   // 매칭 조건 확인 함수
   const isMatched = (
     payment: WithdrawData,
     supra: SupraWithdrawData
   ): boolean => {
     // 1. 사용자 ID 일치 - REACT_APP_NAME과 플랫폼 이름을 고려한 매칭
     const platformNames = ['NEOM', 'ONIC', 'RAINBOW', 'THEBIT', 'GLOBALBIT'];
     const paymentUserId = payment.user_id.toString();
     const supaUserId = supra.userid.toString();
     const appName = process.env.REACT_APP_NAME || '';
     
     // REACT_APP_NAME에 포함된 플랫폼 찾기
     let currentPlatform = '';
     for (const platform of platformNames) {
       if (appName.includes(platform)) {
         currentPlatform = platform;
         break;
       }
     }
     
     // 사용자 ID가 Supra userid 끝에 있는지 확인
     const isUserIdMatched = supaUserId.endsWith(`_${paymentUserId}`) && 
                            currentPlatform && 
                            supaUserId.includes(currentPlatform);
     
     console.log('사용자 ID 매칭 체크:', {
       payment_user_id: paymentUserId,
       supra_userid: supaUserId,
       app_name: appName,
       current_platform: currentPlatform,
       platform_names: platformNames,
       user_id_ends_with: supaUserId.endsWith(`_${paymentUserId}`),
       contains_platform: currentPlatform && supaUserId.includes(currentPlatform),
       is_matched: isUserIdMatched
     });
     
     if (!isUserIdMatched) {
       console.log('❌ 사용자 ID 불일치로 매칭 실패');
       return false;
     }

   // 2. 금액 일치 (Payment API의 KRW amount와 Supra API의 USDT qty 비교)
   const paymentAmountKRW = Number(payment.amount); // KRW
   const supaAmountUSDT = Number(supra.qty); // USDT
   
   // USDT 가격을 1400원으로 가정 (실제로는 API에서 가져와야 함)
   const usdtPrice = 1400; // 1 USDT = 1400 KRW (임시값)
   
   // USDT를 KRW로 환산
   const supaAmountKRW = supaAmountUSDT * usdtPrice;
   
   // 5% 오차 허용 (환율 변동 고려)
   const amountDiff = Math.abs(paymentAmountKRW - supaAmountKRW);
   const allowedDiff = paymentAmountKRW * 0.05; // 5% 허용
   const isAmountMatched = amountDiff <= allowedDiff;
   
   console.log('금액 매칭 체크 (KRW vs USDT):', {
     payment_amount_krw: paymentAmountKRW,
     supra_amount_usdt: supaAmountUSDT,
     supra_amount_krw: supaAmountKRW,
     usdt_price: usdtPrice,
     amount_diff: amountDiff,
     allowed_diff: allowedDiff,
     is_matched: isAmountMatched
   });
   
   if (!isAmountMatched) {
     console.log('❌ 금액 불일치로 매칭 실패 (KRW vs USDT 환산)');
     return false;
   }

     // 3. 시간 근접 (±5분 이내) - 내부 API 시간에 +9시간 추가하여 UTC를 KST로 변환
     const paymentTime = dayjs(payment.created_at).add(9, 'hour'); // 내부 API 시간에 +9시간 추가
     const supaTime = dayjs(supra.created_at);
     const timeDiff = Math.abs(paymentTime.diff(supaTime, 'minute'));
     const isTimeMatched = timeDiff <= 5;

     console.log('시간 매칭 체크:', {
       payment_time_original: payment.created_at,
       payment_time_adjusted: paymentTime.format('YYYY-MM-DD HH:mm:ss'),
       supra_time: supaTime.format('YYYY-MM-DD HH:mm:ss'),
       time_diff_minutes: timeDiff,
       is_matched: isTimeMatched
     });

     if (!isTimeMatched) {
       console.log('❌ 시간 차이로 매칭 실패 (차이:', timeDiff, '분)');
       return false;
     }

     console.log('✅ 모든 조건 통과 - 매칭 성공!');
     return true;
  };

   // 데이터 매칭 및 병합
   const matchAndMergeData = (
     paymentData: WithdrawData[],
     supaData: SupraWithdrawData[]
   ): CombinedWithdrawData[] => {
     console.log('=== 매칭 시작 ===');
     console.log('Payment 데이터 개수:', paymentData.length);
     console.log('Supra 데이터 개수:', supaData.length);
     console.log('Payment 데이터:', paymentData);
     console.log('Supra 데이터:', supaData);
     
     const matchedData: CombinedWithdrawData[] = [];
     const unmatchedPayment: WithdrawData[] = [...paymentData];
     const unmatchedSupra: SupraWithdrawData[] = [...supaData];
     const usedSupraIds = new Set<number>(); // 이미 매칭된 Supra 데이터 ID 추적

     // Payment 데이터를 기준으로 매칭 찾기
     paymentData.forEach((payment, paymentIndex) => {
       console.log(`\n--- Payment ${paymentIndex + 1}/${paymentData.length} 처리 중 ---`);
       console.log('Payment 데이터:', payment);
       
       // 아직 사용되지 않은 Supra 데이터 중에서만 매칭 검색
       const matchingSupraIndex = supaData.findIndex(
         (supra) => {
           const isUsed = usedSupraIds.has(supra.id);
           const matched = !isUsed && isMatched(payment, supra);
           console.log(`Supra ${supra.id} 체크: 사용됨=${isUsed}, 매칭됨=${matched}`);
           return matched;
         }
       );

      if (matchingSupraIndex !== -1) {
        const matchingSupra = supaData[matchingSupraIndex];

        // 매칭된 Supra 데이터를 사용됨으로 표시
        usedSupraIds.add(matchingSupra.id);

        // 매칭된 데이터 생성
        const matched: CombinedWithdrawData = {
          id: payment.id,
          uid: payment.uid,
          user_id: payment.user_id,
          amount: payment.amount,
          order_id: payment.order_id,
          status: payment.status,
          pg_provider: payment.pg_provider,
          pg_tid: payment.pg_tid,
          data: payment.data,
          created_at: payment.created_at,
          updated_at: payment.updated_at,
          completed_at: payment.completed_at,
          source: 'matched',
          level: payment.level,
          group_name: payment.group_name,
          nickname: payment.nickname,
          fullname: payment.fullname,
          email: payment.email,
          phone: payment.phone,
          loginid: payment.loginid,
          userInfo: payment.userInfo,
          // Supra 추가 정보
          request_id: matchingSupra.request_id,
          send_qty: matchingSupra.send_qty,
          out_fee: matchingSupra.out_fee,
          reason: matchingSupra.reason,
          payment_id: payment.id,
          supra_id: matchingSupra.id,
          supra_status: matchingSupra.status,
          supra_updated_at: matchingSupra.updated_at,
          otc_link: matchingSupra.otc_link,
        };

        matchedData.push(matched);

        // 매칭된 항목들을 미매칭 리스트에서 제거
        const paymentIdx = unmatchedPayment.findIndex(
          (item) => item.id === payment.id
        );
        if (paymentIdx !== -1) unmatchedPayment.splice(paymentIdx, 1);

        const supaIdx = unmatchedSupra.findIndex(
          (item) => item.id === matchingSupra.id
        );
        if (supaIdx !== -1) unmatchedSupra.splice(supaIdx, 1);
      }
    });

    // 매칭되지 않은 Payment 데이터 추가
    const unmatchedPaymentTransformed: CombinedWithdrawData[] = unmatchedPayment.map((item) => ({
      ...item,
      source: 'payment' as const,
    }));

    // 매칭되지 않은 Supra 데이터 추가
    const unmatchedSupraTransformed: CombinedWithdrawData[] = unmatchedSupra.map((item) => ({
      id: `supra_${item.id}`,
      user_id: item.userid,
      amount: item.qty,
      status: item.status,
      created_at: item.created_at,
      updated_at: item.updated_at,
      source: 'supra' as const,
      request_id: item.request_id,
      send_qty: item.send_qty,
      out_fee: item.out_fee,
      reason: item.reason,
      supra_id: item.id,
      supra_status: item.status,
      supra_updated_at: item.updated_at,
      otc_link: item.otc_link,
    }));

    console.log('매칭된 데이터 개수:', matchedData.length);
    console.log('매칭되지 않은 Payment 데이터 개수:', unmatchedPayment.length);
    console.log('매칭되지 않은 Supra 데이터 개수:', unmatchedSupra.length);
    
    // showOnlyMatched 옵션에 따라 반환할 데이터 결정
    if (showOnlyMatched) {
      return matchedData; // 매칭된 데이터만 반환
    } else {
      // 모든 데이터 병합
      return [...matchedData, ...unmatchedPaymentTransformed, ...unmatchedSupraTransformed];
    }
  };

  // Payment API와 Supra API를 동시에 호출하여 데이터를 병합하는 함수
  const fetchAllData = async () => {
    if (!token) return;

    setLoading(true);
    try {
      // 1. Payment API 호출
      let paymentUrl = `/payment/admin?request_type=WITHDRAW&limit=20&offset=0`;

      if (userId) {
        paymentUrl += `&user_id=${userId}`;
      }

      if (status) {
        paymentUrl += `&status=${status}`;
      }

      if (searchWord) {
        // 검색 타입에 따라 올바른 API 파라미터 사용
        if (searchType === 'id') {
          paymentUrl += `&search_user_code=${searchWord}`;
        } else if (searchType === 'loginid') {
          paymentUrl += `&search_login_id=${searchWord}`;
        } else if (searchType === 'fullname') {
          paymentUrl += `&search_real_name=${searchWord}`;
        } else {
          paymentUrl += `&search=${searchWord}`;
        }
      }

      console.log('Payment API URL:', paymentUrl);

      // 2. Supra API 호출
      let supaUrl = 'https://api-v2.supra-pay.com/withdrawal/getlist';
      const supaParams = new URLSearchParams();

      // 날짜가 설정되지 않은 경우 기본값 설정 (2025년 6월부터 오늘까지)
      const defaultEndDate = dayjs().format('YYYY-MM-DD');
      const defaultStartDate = '2025-06-01';

      const fromDate = defaultStartDate;
      const toDate = endDate || defaultEndDate;

      supaParams.append('from_date', fromDate);
      supaParams.append('to_date', toDate);
      supaParams.append('limit', '100');
      supaUrl += `?${supaParams.toString()}`;

      // 두 API 동시 호출
      const [paymentResponse, supaResponse] = await Promise.allSettled([
        API.get(paymentUrl, token, {}, false, API_URL),
        fetch(supaUrl, {
          method: 'GET',
          headers: {
            'authorization-key': SUPRA_AUTH_KEY,
            accept: '*/*',
          },
        }),
      ]);

      let paymentData: WithdrawData[] = [];
      let supaData: SupraWithdrawData[] = [];

      // Payment API 응답 처리
      if (
        paymentResponse.status === 'fulfilled' &&
        (paymentResponse.value.code === 0 || paymentResponse.value.success)
      ) {
        paymentData = paymentResponse.value.result.data || [];
      } else if (paymentResponse.status === 'rejected') {
        console.error('Payment API 오류:', paymentResponse.reason);
      }

      // Supra API 응답 처리
      if (supaResponse.status === 'fulfilled' && supaResponse.value.ok) {
        const responseData = await supaResponse.value.json();
        // 응답 데이터가 배열인지 확인하고 안전하게 처리
        supaData = Array.isArray(responseData)
          ? responseData
          : responseData.data && Array.isArray(responseData.data)
          ? responseData.data
          : [];
      } else if (supaResponse.status === 'rejected') {
        console.error('Supra API 오류:', supaResponse.reason);
        supaData = []; // 오류 발생 시 빈 배열로 초기화
      } else if (
        supaResponse.status === 'fulfilled' &&
        !supaResponse.value.ok
      ) {
        console.error(
          'Supra API HTTP 오류:',
          supaResponse.value.status,
          supaResponse.value.statusText
        );
        supaData = []; // 오류 발생 시 빈 배열로 초기화
      }

      // 고유한 user_id 목록 추출 (Payment 데이터에서)
      const userIds = [...new Set(paymentData.map((item: any) => item.user_id))];
      // 사용자 정보 가져오기
      const userInfoMap = await fetchUserInfo(userIds as number[]);
      
      // Payment 데이터에 사용자 정보 매칭
      const enrichedPaymentData = paymentData.map((item: any) => ({
        ...item,
        userInfo: userInfoMap[item.user_id] || null
      }));

      // 데이터 매칭 및 병합
      const combinedData = matchAndMergeData(enrichedPaymentData, supaData);

      // 날짜순 정렬 (최신순)
      combinedData.sort(
        (a, b) =>
          new Date(b.created_at).getTime() -
          new Date(a.created_at).getTime()
      );

      // 전체 데이터 저장
      setAllCombinedData(combinedData);
      setTotalCount(combinedData.length);
    } catch (error) {
      console.error('출금 요청 목록 조회 오류:', error);
      // 데이터가 없는 경우에는 오류 메시지를 표시하지 않음
      if (error instanceof Error && error.message !== 'No data available') {
        message.error('출금 요청 목록을 불러오는 중 오류가 발생했습니다');
      }
    } finally {
      setLoading(false);
    }
  };

  // 현재 페이지에 해당하는 데이터 계산
  const getCurrentPageData = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return allCombinedData.slice(startIndex, endIndex);
  };

  // 페이지네이션을 위한 fetchWithdrawRequests 함수 (기존 함수명 유지)
  const fetchWithdrawRequests = async (page = 1, size = 20) => {
    setPage(page);
    setPageSize(size);

    // 전체 데이터가 없거나 필터 조건이 변경된 경우 새로 조회
    if (allCombinedData.length === 0) {
      await fetchAllData();
    } else {
      // 현재 페이지 데이터만 업데이트
      setData(getCurrentPageData());
    }
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchAllData();
  }, [status, showOnlyMatched]);

  // allCombinedData가 변경될 때마다 현재 페이지 데이터 업데이트
  useEffect(() => {
    setData(getCurrentPageData());
  }, [allCombinedData, page, pageSize]);

  // 검색 함수
  const handleSearch = () => {
    setPage(1);
    fetchAllData(); // 직접 데이터 새로고침
  };

  // 수동 출금요청 성공 콜백
  const handleManualWithdrawalSuccess = () => {
    setAllCombinedData([]); // 전체 데이터 초기화
    fetchAllData(); // 데이터 새로고침
  };

  // 페이지 변경 시 데이터 로드
  const handleTableChange = (page: number, size: number) => {
    setPage(page);
    setPageSize(size);
    // 페이지 변경 시에는 새로 데이터를 로드할 필요 없음 (useEffect에서 자동 처리)
  };

  // Payment API를 사용한 승인 처리
  const handleApprove = async (record: CombinedWithdrawData) => {
    if (!token) return;

    try {
      setLoading(true);

      // Payment API가 있는 경우에만 승인 처리
      if (record.source === 'payment' || record.source === 'matched') {
        const response = await API.put(
          `/payment/admin/${record.id}`,
          token,
          {
            status: 'APPROVED',
            data: {
              admin_note: '관리자에 의해 승인됨',
              approval_time: new Date().toISOString()
            }
          },
          API_URL
        );

        if (!(response.code === 0 || response.success)) {
          message.error(
            response.message || '내부 승인 처리에 실패했습니다'
          );
          return;
        }
      }

      message.success('출금 요청이 승인되었습니다');
      setAllCombinedData([]); // 전체 데이터 초기화
      fetchAllData();
    } catch (error) {
      console.error('승인 처리 오류:', error);
      message.error('승인 처리 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  // PG 승인 처리
  const handlePGApprove = async (record: CombinedWithdrawData) => {
    if (!token) return;

    if (!record.request_id) {
      message.warning('PG 요청 ID가 없어 PG 승인을 할 수 없습니다');
      return;
    }

    try {
      setLoading(true);

      const supaApproveUrl = `https://api-v2.supra-pay.com/withdrawal/approve/${record.request_id}`;
      const supaResponse = await fetch(supaApproveUrl, {
        method: 'POST',
        headers: {
          'authorization-key': SUPRA_AUTH_KEY,
          accept: '*/*',
          'content-length': '0',
        },
      });

      if (supaResponse.ok) {
        console.log('Supra API 승인 성공:', record.request_id);
        message.success('PG 승인 처리가 완료되었습니다');
        setAllCombinedData([]); // 전체 데이터 초기화
        fetchAllData();
      } else {
        console.error(
          'Supra API 승인 실패:',
          supaResponse.status,
          supaResponse.statusText
        );
        const errorText = await supaResponse.text();
        console.error('Supra API 오류 내용:', errorText);
        message.error('PG 승인 처리에 실패했습니다');
      }
    } catch (error) {
      console.error('PG 승인 처리 오류:', error);
      message.error('PG 승인 처리 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  // Payment API를 사용한 취소 처리
  const handleCancel = async (record: CombinedWithdrawData) => {
    if (!token) return;

    try {
      setLoading(true);

      // Payment API가 있는 경우에만 취소 처리
      if (record.source === 'payment' || record.source === 'matched') {
        const response = await API.put(
          `/payment/admin/${record.id}`,
          token,
          {
            status: 'CANCELLED',
            data: {
              admin_note: '관리자에 의해 취소됨',
              cancellation_time: new Date().toISOString()
            }
          },
          API_URL
        );

        if (!(response.code === 0 || response.success)) {
          message.error(response.message || '내부 취소 처리에 실패했습니다');
          return;
        }
      }

      // Supra API 취소 처리 (request_id가 있는 경우에만)
      if (record.request_id) {
        try {
          const supaCancelUrl = `https://api-v2.supra-pay.com/withdrawal/cancel/${record.request_id}`;
          const supaResponse = await fetch(supaCancelUrl, {
            method: 'POST',
            headers: {
              'authorization-key': SUPRA_AUTH_KEY,
              accept: '*/*',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              actor_id: `${record.user_id}`,
              actor_type: 'user',
            }),
          });

          if (supaResponse.ok) {
            message.success('내부 시스템 및 Supra 모두 취소 처리되었습니다');
          } else {
            console.error(
              'Supra API 취소 실패:',
              supaResponse.status,
              supaResponse.statusText
            );
            const errorText = await supaResponse.text();
            console.error('Supra API 오류 내용:', errorText);
            message.warning(
              '내부 시스템은 취소되었지만 Supra 취소에 실패했습니다'
            );
          }
        } catch (supaError) {
          console.error('Supra API 취소 오류:', supaError);
          message.warning(
            '내부 시스템은 취소되었지만 Supra 취소 중 오류가 발생했습니다'
          );
        }
      } else {
        message.success('출금 요청이 취소되었습니다');
      }

      // 데이터 새로고침
      setAllCombinedData([]); // 전체 데이터 초기화
      fetchAllData();
    } catch (error) {
      console.error('취소 처리 오류:', error);
      message.error('취소 처리 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (record: CombinedWithdrawData) => {
    if (!token) return;

    try {
      setLoading(true);

      // Payment API가 있는 경우에만 거절 처리
      if (record.source === 'payment' || record.source === 'matched') {
        const response = await API.put(
          `/payment/admin/${record.id}`,
          token,
          {
            status: 'REJECTED',
            data: {
              admin_note: '관리자에 의해 거절됨',
              rejection_time: new Date().toISOString()
            }
          },
          API_URL
        );

        if (!(response.code === 0 || response.success)) {
          message.error(response.message || '내부 거절 처리에 실패했습니다');
          return;
        }
      }

      // Supra API 거절 처리 (request_id가 있고 supra_status가 'canceled' 또는 'otc_canceled'가 아닌 경우에만)
      if (record.request_id && record.supra_status !== 'canceled' && record.supra_status !== 'otc_canceled') {
        try {
          const supaRejectUrl = `https://api-v2.supra-pay.com/withdrawal/reject/${record.request_id}`;
          const supaResponse = await fetch(supaRejectUrl, {
            method: 'POST',
            headers: {
              'authorization-key': SUPRA_AUTH_KEY,
              accept: '*/*',
              'content-length': '0',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              reason: '거절',
            }),
          });

          if (supaResponse.ok) {
            message.success('내부 시스템 및 Supra 모두 거절 처리되었습니다');
          } else {
            console.error(
              'Supra API 거절 실패:',
              supaResponse.status,
              supaResponse.statusText
            );
            const errorText = await supaResponse.text();
            console.error('Supra API 오류 내용:', errorText);
            message.warning(
              '내부 시스템은 거절되었지만 Supra 거절에 실패했습니다'
            );
          }
        } catch (supaError) {
          console.error('Supra API 거절 오류:', supaError);
          message.warning(
            '내부 시스템은 거절되었지만 Supra 거절 중 오류가 발생했습니다'
          );
        }
      } else {
        message.success('출금 요청이 거절되었습니다');
      }

      // 데이터 새로고침
      setAllCombinedData([]); // 전체 데이터 초기화
      fetchAllData();
    } catch (error) {
      console.error('거절 처리 오류:', error);
      message.error('거절 처리 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
     }
   };

   // 판매금액과 USDT 가격 수정 함수
   const handleUpdatePrices = async (saleAmount: number, usdtPrice: number) => {
     if (!token || !selectedRecord) return;

     try {
       setLoading(true);

       // 기존 data에 새로운 값들 추가
       const updatedData = {
         ...selectedRecord.data,
         sale_amount: saleAmount,
         usdt_price: usdtPrice,
         price_updated_time: new Date().toISOString(),
         price_updated_by: 'admin'
       };

       const response = await API.put(
         `/payment/admin/${selectedRecord.id}`,
         token,
         {
           data: updatedData
         },
         API_URL
       );

       if (response.code === 0 || response.success) {
         message.success('판매금액과 USDT 가격이 업데이트되었습니다');
         setShowPriceEditModal(false);
         setSelectedRecord(null);
         
         // 데이터 새로고침
         setAllCombinedData([]);
         fetchAllData();
       } else {
         message.error(response.message || '가격 업데이트에 실패했습니다');
       }
     } catch (error) {
       console.error('가격 업데이트 오류:', error);
       message.error('가격 업데이트 중 오류가 발생했습니다');
     } finally {
       setLoading(false);
     }
   };

   // 불필요한 복잡한 수정 기능들 제거됨

  const columns: ColumnsType<CombinedWithdrawData> = [
    {
      title: '출금요청ID',
      dataIndex: 'id',
      key: 'id',
      align: 'center',
      render: (value: string | number, record: CombinedWithdrawData) => (
        <div>
          <div>{value}</div>
          {record.source === 'supra' && record.request_id && (
            <div style={{ fontSize: '11px', color: '#666' }}>
              {record.request_id.substring(0, 8)}...
            </div>
          )}
        </div>
      ),
    },
    {
      title: '회원',
      key: 'uid',
      align: 'center',
      render: (text, data) => (
        <UserTag 
          uid={data?.userInfo?.uid || data?.uid} 
          userid={data?.user_id} 
          level={data?.userInfo?.level || data?.level}
          nickname={data?.userInfo?.fullname || data?.fullname}
        />
      ),
    },
    {
      title: '이름',
      align: 'center',
      dataIndex: 'nickname',
      render: (text, data) => (
        <View>
          <Text style={{ justifyContent: 'flex-start' }}>
            {data?.userInfo?.nickname || data?.nickname || '-'}
          </Text>
          <Text style={{ justifyContent: 'flex-start' }}>
            ({data?.userInfo?.fullname || data?.fullname || '-'})
          </Text>
        </View>
      ),
    },
    {
      title: '아이디',
      align: 'center',
      dataIndex: 'loginid',
      render: (text, data) => (
        <Text style={{ justifyContent: 'flex-start' }}>
          {data?.userInfo?.loginid || data?.loginid || '-'}
        </Text>
      ),
    },
    {
      title: '그룹',
      align: 'center',
      dataIndex: 'group_name',
      render: (text, data) => {
        const groupName = data?.userInfo?.group_name || data?.group_name;
        return groupName ? (
          <View>
            <Button>{groupName}</Button>
          </View>
        ) : (
          <Text>-</Text>
        );
      },
    },
     {
       title: '출금 금액',
       dataIndex: 'amount',
       key: 'amount',
       align: 'center',
       render: (value: string, record: CombinedWithdrawData) => (
         <div>
           <KrwPrice price={Number(value) || 0} />
           {record.source === 'supra' && record.send_qty && (
             <div style={{ fontSize: '11px', color: '#666' }}>
               실송금: {Number(record.send_qty).toFixed(2)} USDT
             </div>
           )}
         </div>
       ),
     },
     {
       title: '판매금액',
       key: 'sale_amount',
       align: 'center',
       render: (_, record: CombinedWithdrawData) => {
         const saleAmount = record.data?.sale_amount;
         return (
           <div 
             style={{ cursor: 'pointer', color: '#1890ff' }}
             onClick={(e) => {
               e.stopPropagation();
               setSelectedRecord(record);
               setShowPriceEditModal(true);
             }}
           >
             {saleAmount ? (
               <KrwPrice price={Number(saleAmount)} />
             ) : (
               <span style={{ color: '#999' }}>설정하기</span>
             )}
           </div>
         );
       },
     },
     {
       title: 'USDT 가격',
       key: 'usdt_price',
       align: 'center',
       render: (_, record: CombinedWithdrawData) => {
         const usdtPrice = record.data?.usdt_price;
         return (
           <div 
             style={{ cursor: 'pointer', color: '#1890ff' }}
             onClick={(e) => {
               e.stopPropagation();
               setSelectedRecord(record);
               setShowPriceEditModal(true);
             }}
           >
             {usdtPrice ? (
               <span>{Number(usdtPrice).toLocaleString()}원</span>
             ) : (
               <span style={{ color: '#999' }}>설정하기</span>
             )}
           </div>
         );
       },
     },
    {
      title: 'PG 제공자',
      dataIndex: 'pg_provider',
      key: 'pg_provider',
      align: 'center',
      render: (value: string) => value || '-',
    },
     {
       title: '계좌 정보',
       align: 'center',
       render: (_, record: CombinedWithdrawData) => {
         // 회원정보에서 계좌 정보 가져오기
         const userInfo = record.userInfo;
         const bankName = userInfo?.bank_name;
         const accountNumber = userInfo?.account_number;
         const accountHolder = userInfo?.account_holder;
         
         if (bankName || accountNumber || accountHolder) {
           return (
             <div>
               <div>{bankName || '-'}</div>
               <div>{accountNumber || '-'}</div>
               <div>{accountHolder || '-'}</div>
             </div>
           );
         }
         return '-';
       },
     },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (value: string, record: CombinedWithdrawData) => {
        let statusText = value;
        switch (value?.toUpperCase()) {
          case 'PENDING':
            statusText = '대기';
            break;
          case 'APPROVED':
            statusText = '승인';
            break;
          case 'REJECTED':
            statusText = '거절';
            break;
          case 'PROCESSING':
            statusText = '처리중';
            break;
          case 'COMPLETED':
            statusText = '완료';
            break;
          case 'FAILED':
            statusText = '실패';
            break;
          case 'CANCELLED':
            statusText = '취소';
            break;
          default:
            statusText = value;
        }

        return (
          <div>
            <div>{statusText}</div>
            {record.supra_status && (
              <div
                style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}
              >
                <span style={{ fontWeight: 'bold' }}>Supra: </span>
                {record.supra_status === 'pending' ? (
                  '대기'
                ) : record.supra_status === 'approved' ? (
                  <span>
                    승인
                    {record.otc_link && (
                      <a
                        href={record.otc_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          marginLeft: '5px',
                          color: '#1890ff',
                          textDecoration: 'underline',
                          fontSize: '10px',
                        }}
                      >
                        [OTC 링크]
                      </a>
                    )}
                  </span>
                ) : record.supra_status === 'canceled' ? (
                  '취소'
                ) : (
                  record.supra_status
                )}
              </div>
            )}
            {record.reason && (
              <div
                style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}
              >
                사유: {record.reason}
              </div>
            )}
          </div>
        );
      },
    },
     {
       title: '요청일시',
       dataIndex: 'created_at',
       key: 'created_at',
       align: 'center',
       render: (value: string, record: CombinedWithdrawData) => {
         if (!value) return '-';
         
         // 내부 API 데이터인 경우 +9시간 적용
         if (record.source === 'payment' || record.source === 'matched') {
           return dayjs(value).add(9, 'hour').format('YYYY-MM-DD HH:mm:ss');
         }
         
         // Supra API 데이터는 그대로 표시
         return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
       },
     },
     {
       title: '완료일시',
       dataIndex: 'completed_at',
       key: 'completed_at',
       align: 'center',
       render: (value: string, record: CombinedWithdrawData) => {
         // completed_at이 있으면 사용
         if (value) {
           return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
         }
         
         // 상태에 따라 다른 시간 필드 사용
         let completedTime = null;
         if (record.status?.toUpperCase() === 'APPROVED') {
           completedTime = record.data?.approval_time;
         } else if (record.status?.toUpperCase() === 'REJECTED') {
           completedTime = record.data?.rejection_time;
         } else if (record.status?.toUpperCase() === 'CANCELLED') {
           completedTime = record.data?.cancellation_time;
         }
         
         return completedTime
           ? dayjs(completedTime).format('YYYY-MM-DD HH:mm:ss')
           : '-';
       },
     },

    {
      title: t('Action'),
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <Space size={5}>
          <Button
            type="primary"
            onClick={() => handleApprove(record)}
            disabled={
              record.status?.toUpperCase() === 'APPROVED' ||
              (record.supra_status !== 'approved' &&
                record.supra_status !== 'completed')
            }
            title={
              record.status?.toUpperCase() === 'APPROVED'
                ? '이미 내부 승인이 완료되었습니다'
                : record.supra_status !== 'approved' &&
                  record.supra_status !== 'completed'
                ? 'PG 승인 또는 완료되어야 합니다'
                : ''
            }
          >
            승인
          </Button>
          <Button
            type="default"
            onClick={() => handlePGApprove(record)}
            disabled={
              record.status?.toUpperCase() === 'APPROVED' ||
              !['PENDING'].includes(record.status?.toUpperCase()) ||
              !record.request_id ||
              record.supra_status === 'approved' ||
              record.supra_status === 'completed' ||
              record.supra_status === 'processing'
            }
            title={
              record.status?.toUpperCase() === 'APPROVED'
                ? '내부 승인 완료 후에는 PG 승인할 수 없습니다'
                : !record.request_id
                ? 'PG 요청 ID가 없습니다'
                : record.supra_status === 'processing'
                ? 'PG에서 처리 중입니다'
                : record.supra_status === 'approved' ||
                  record.supra_status === 'completed'
                ? '이미 PG 처리가 완료되었습니다'
                : ''
            }
          >
            PG 승인
          </Button>
          <Button
            danger
            onClick={() => handleReject(record)}
            disabled={
              record.status?.toUpperCase() === 'APPROVED' ||
              !['PENDING'].includes(record.status?.toUpperCase()) ||
              record.supra_status === 'completed'
            }
            title={
              record.status?.toUpperCase() === 'APPROVED'
                ? '내부 승인 완료 후에는 거절할 수 없습니다'
                : ''
            }
          >
            거절
          </Button>
          <Button
            onClick={() => handleCancel(record)}
            disabled={
              record.status?.toUpperCase() === 'APPROVED' ||
              (!['PENDING'].includes(record.status?.toUpperCase()) && record.status?.toUpperCase() !== 'APPROVED') ||
              record.supra_status === 'approved' ||
              record.supra_status === 'completed' ||
              record.supra_status === 'processing'
            }
            title={
              record.status?.toUpperCase() === 'APPROVED'
                ? '내부 승인 완료 후에는 취소할 수 없습니다'
                : record.supra_status === 'processing'
                ? 'PG에서 처리 중일 때는 취소할 수 없습니다'
                : record.supra_status === 'approved' ||
                  record.supra_status === 'completed'
                ? 'PG 처리 후에는 취소할 수 없습니다'
                : ''
            }
          >
            취소
          </Button>
        </Space>
      ),
    },
  ];

  const items = [
    // {
    //   title: '전체',
    //   link: '/cash/pg',
    // },
    {
      title: 'PG 입금 관리',
      link: '/cash/pg/deposit',
    },
    {
      title: 'PG 출금 관리',
      link: '/cash/pg/withdraw',
    },
  ];

  const onRow = (e: any, record: any) => {
    const index = e.target?.cellIndex
      ? e.target?.cellIndex
      : e.target?.offsetParent?.cellIndex;
    if (index < 9) {
      setSelectedData(record?.user_id);
      if (selectedData) {
        setShowModal(!showModal);
      }
    }
  };

  return (
    <Space direction='vertical' size={0} style={{ width: "100%" }}>
        <ModernTabNavigation items={items} />
        <View style={{ width: '100%', paddingTop: '10px' }}>
          <View
            style={{
              width: '100%',
              padding: 10,
              flexDirection: 'row',
              background: '#fff',
              marginTop: 5,
              marginBottom: 5,
              borderRadius: 10,
              flex: 1,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
             <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
              <Select
                style={{
                  minWidth: 150,
                }}
                value={status}
                onChange={setStatus}
                options={[
                  { value: '', label: '전체' },
                  { value: 'PENDING', label: '신청' },
                  { value: 'APPROVED', label: '완료' },
                  { value: 'REJECTED', label: '거절' },
                  { value: 'FAILED', label: '실패' },
                ]}
              />
              <Search
                addonBefore={
                  <Select
                    style={{
                      minWidth: 120,
                    }}
                    value={searchType}
                    onChange={setSearchType}
                    options={[
                      { value: 'id', label: '회원코드' },
                      { value: 'loginid', label: '로그인아이디' },
                      { value: 'fullname', label: '실명' },
                    ]}
                  />
                }
                placeholder="검색어를 입력해주세요"
                value={searchWord}
                onChange={(e) => setSearchWord(e.target.value)}
                onSearch={handleSearch}
                style={{ width: 400 }}
              />
             </View>
            <Space>
              {/* <Button
                type={showOnlyMatched ? "primary" : "default"}
                onClick={() => {
                  setShowOnlyMatched(!showOnlyMatched);
                  setAllCombinedData([]); // 데이터 초기화하여 새로고침
                }}
              >
                {showOnlyMatched ? "매칭된 데이터만" : "모든 데이터"}
              </Button> */}
              <Button
                type="primary"
                onClick={() => setShowManualWithdrawalModal(true)}
              >
                수동 출금요청
              </Button>
            </Space>
            {/*<Space>*/}
            {/*           <Space.Compact>*/}
            {/*    <DatePicker*/}
            {/*      onChange={e =>*/}
            {/*        setStartDate(*/}
            {/*            e ? dayjs(e).format('YYYY-MM-DD') : null*/}
            {/*        )*/}
            {/*      }*/}
            {/*        value={*/}
            {/*        startDate*/}
            {/*          ? dayjs(startDate, 'YYYY-MM-DD')*/}
            {/*            : null*/}
            {/*      }*/}
            {/*        placeholder="시작일"*/}
            {/*    />*/}
            {/*    <DatePicker*/}
            {/*      onChange={e =>*/}
            {/*        setEndDate(*/}
            {/*            e ? dayjs(e).format('YYYY-MM-DD') : null*/}
            {/*        )*/}
            {/*      }*/}
            {/*        value={*/}
            {/*        endDate*/}
            {/*          ? dayjs(endDate, 'YYYY-MM-DD')*/}
            {/*            : null*/}
            {/*      }*/}
            {/*        placeholder="종료일"*/}
            {/*    />*/}
            {/*             <Button type="primary" onClick={handleSearch}>검색</Button>*/}
            {/*  </Space.Compact>*/}
            {/*</Space>*/}
          </View>

          <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            rowKey="id"
            style={{ width: '100%' }}
            onRow={(record) => ({
              onClick: (e) => onRow(e, record),
            })}
            pagination={{
              total: totalCount,
              current: page,
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total) => `총 ${total}건`,
              onChange: handleTableChange,
            }}
          />
        </View>

      <AdminUserModal
        showModal={showModal}
        setShowModal={setShowModal}
        selectedData={selectedData}
      />

       <ManualWithdrawalModal
         isOpen={showManualWithdrawalModal}
         onClose={() => setShowManualWithdrawalModal(false)}
         onSuccess={handleManualWithdrawalSuccess}
       />

       {/* 가격 수정 모달 */}
       <Modal
         title="판매금액 및 USDT 가격 설정"
         open={showPriceEditModal}
         onCancel={() => {
           setShowPriceEditModal(false);
           setSelectedRecord(null);
         }}
         footer={null}
         width={500}
       >
         {selectedRecord && (
           <PriceEditForm
             record={selectedRecord}
             onSubmit={handleUpdatePrices}
             onCancel={() => {
               setShowPriceEditModal(false);
               setSelectedRecord(null);
             }}
           />
         )}
       </Modal>
     </Space>
   );
 };

 // 가격 수정 폼 컴포넌트
 const PriceEditForm: React.FC<{
   record: CombinedWithdrawData;
   onSubmit: (saleAmount: number, usdtPrice: number) => void;
   onCancel: () => void;
 }> = ({ record, onSubmit, onCancel }) => {
   const [form] = Form.useForm();
   const [loading, setLoading] = useState(false);

   const handleSubmit = async (values: any) => {
     setLoading(true);
     try {
       await onSubmit(values.saleAmount, values.usdtPrice);
     } finally {
       setLoading(false);
     }
   };

   return (
     <Form
       form={form}
       layout="vertical"
       onFinish={handleSubmit}
       initialValues={{
         saleAmount: record.data?.sale_amount || '',
         usdtPrice: record.data?.usdt_price || 1400,
       }}
     >
       <div style={{ marginBottom: 16 }}>
         <div style={{ fontWeight: 'bold', marginBottom: 8 }}>출금 요청 정보</div>
         <div>출금 ID: {record.id}</div>
         <div>사용자 ID: {record.user_id}</div>
         <div>출금 금액: <KrwPrice price={Number(record.amount)} /></div>
       </div>

       <Form.Item
         label="판매금액 (KRW)"
         name="saleAmount"
         rules={[
           { required: true, message: '판매금액을 입력해주세요' },
           { type: 'number', min: 0, message: '0 이상의 값을 입력해주세요' }
         ]}
       >
         <InputNumber
           style={{ width: '100%' }}
           placeholder="판매금액을 입력하세요"
           formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
           parser={(value) => value?.replace(/,/g, '') as any}
           addonAfter="원"
         />
       </Form.Item>

       <Form.Item
         label="USDT 가격"
         name="usdtPrice"
         rules={[
           { required: true, message: 'USDT 가격을 입력해주세요' },
           { type: 'number', min: 0, message: '0 이상의 값을 입력해주세요' }
         ]}
       >
         <InputNumber
           style={{ width: '100%' }}
           placeholder="USDT 가격을 입력하세요"
           formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
           parser={(value) => value?.replace(/,/g, '') as any}
           addonAfter="원"
         />
       </Form.Item>

       <Form.Item>
         <Space>
           <Button type="primary" htmlType="submit" loading={loading}>
             저장
           </Button>
           <Button onClick={onCancel}>
             취소
           </Button>
         </Space>
       </Form.Item>
     </Form>
   );
 };
 
 export default PGWithdrawPage;
